import React, { useState, useEffect } from 'react';
import { apiRequest } from '../../utils/apiHelper';

import { triggerNotificationRefresh } from '../../utils/notificationsApi';
import { SHIPMENT_STATUS_TEXT, SHIPMENT_STATUS_STYLES } from '../../constants/orderConstants';
import ShipmentStatusWithInfo from '../../components/ShipmentStatusWithInfo';
import { useCompany } from '../../context/CompanyContext';
import JsBarcode from 'jsbarcode';
import placeholderImage from '../../assets/images/placeholder.jpg';
import ActionDrawer from '../components/shared/ActionDrawer';
import { showSuccess, showError, showServerError } from '../../utils/toastHelper';

function ShipmentModal({ isOpen, order: initialOrder, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [carriers, setCarriers] = useState([]);
  const [currentOrder, setCurrentOrder] = useState(initialOrder);
  const { activeCompany } = useCompany();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [formData, setFormData] = useState({
    carrierId: '',
    packageDetails: {
      weight: '',
      size: '',
      description: ''
    }
  });

  useEffect(() => {
    if (isOpen && initialOrder) {
      loadCarriers();
      if (!initialOrder.shipment || (initialOrder.shipment && (initialOrder.shipment.status === 'CANCELLED' || initialOrder.shipment.status === 'PENDING'))) {
        checkOrderShipment();
      } else {
        setCurrentOrder(initialOrder);
      }
    }
  }, [isOpen, initialOrder]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const checkOrderShipment = async () => {
    try {
      const response = await apiRequest(`/shipping/orders/${initialOrder.id}`);
      if (response.success) {
        setCurrentOrder({
          ...currentOrder,
          orderNumber: response.shipment?.order?.orderNumber || currentOrder.orderNumber,
          shippingAddress: response.shipment?.order?.shippingAddress || currentOrder.shippingAddress,
          products: response.shipment?.order?.products || currentOrder.products,
          createdAt: response.shipment?.order?.createdAt || currentOrder.createdAt,
          shipment: response.shipment ? {
            ...response.shipment,
            carrier: {
              id: response.shipment?.carrier?.id,
              name: response.shipment?.carrier?.name,
              code: response.shipment?.carrier?.code
            }
          } : null
        });
      } else {
        throw new Error(response.message || 'Sipariş kargo bilgileri yüklenirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Failed to fetch order shipment:', error);
      if (error.apiError) showServerError();
      else showError(error.message || 'Sipariş kargo bilgileri yüklenirken bir hata oluştu');
      setCurrentOrder(initialOrder);
    }
  };

  const loadCarriers = async () => {
    try {
      const response = await apiRequest('/shipping/carriers');
      if (response.success) {
        setCarriers(response.carriers);
        if (response.carriers.length > 0) {
          setFormData(prev => ({
            ...prev,
            carrierId: response.carriers[0].id
          }));
        }
      } else {
        throw new Error(response.message || 'Kargo firmaları yüklenirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Failed to load carriers:', error);
      showError(error.message || 'Kargo firmaları yüklenirken bir hata oluştu');
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    if (!formData.carrierId) {
      showError('Lütfen kargo firması seçin');
      return;
    }

    try {
      setLoading(true);
      const response = await apiRequest(`/shipping/orders/${initialOrder.id}/carrier`, {
        method: 'POST',
        body: JSON.stringify(formData)
      });

      if (response.success) {
        const shipmentData = response.shipment || {};
        const updatedOrder = {
          ...currentOrder,
          shipment: {
            ...currentOrder.shipment,
            id: shipmentData.id,
            trackingNumber: shipmentData.trackingNumber || '',
            barcode: shipmentData.barcode || '',
            carrier: shipmentData.carrier || carriers.find(c => c.id === formData.carrierId) || {},
            packageDetails: shipmentData.packageDetails || formData.packageDetails || {},
            status: shipmentData.status || 'LABEL_CREATED',
            trackingHistory: shipmentData.trackingHistory || []
          }
        };
        setCurrentOrder(updatedOrder);
        showSuccess('Kargo gönderisi başarıyla oluşturuldu');
        onSuccess(updatedOrder.shipment, true);
        setTimeout(() => triggerNotificationRefresh(), 1000);
        setTimeout(() => {
          if (response.shipment.barcode) printBarcode(response.shipment.barcode, updatedOrder);
        }, 500);
      } else {
        throw new Error(response.message || 'Kargo gönderisi oluşturulurken bir hata oluştu');
      }
    } catch (error) {
      console.error('Failed to create shipment:', error);
      if (error.apiError) showServerError();
      else showError(error.message || 'Kargo gönderisi oluşturulurken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const printBarcode = (barcodeValue, orderData = null) => {
    if (!barcodeValue) {
      showError('Barkod bilgisi bulunamadı');
      return;
    }

    const orderToUse = orderData || currentOrder;
    const trackingNumber = orderToUse?.shipment?.trackingNumber || '';
    const barcodeNumber = orderToUse?.shipment?.barcode || barcodeValue;
    const carrierCode = orderToUse?.shipment?.carrier?.code || '';

    const hasTwoBarcodes = carrierCode === 'AK' && trackingNumber && barcodeNumber && trackingNumber !== barcodeNumber;
    const trackingLabel = carrierCode === 'AK' ? 'Entegrasyon No' : 'Takip No';
    const barcodeLabel = carrierCode === 'AK' ? 'Paket Barkod No' : 'Barkod No';

    let integrationBarcodeBase64 = '';
    let packageBarcodeBase64 = '';
    try {
      if (hasTwoBarcodes) {
        const integrationCanvas = document.createElement('canvas');
        JsBarcode(integrationCanvas, trackingNumber, {
          format: "CODE128", width: 1.5, height: 40, displayValue: false, margin: 5
        });
        integrationBarcodeBase64 = integrationCanvas.toDataURL("image/png");
      }
      const packageCanvas = document.createElement('canvas');
      JsBarcode(packageCanvas, barcodeNumber, {
        format: "CODE128", width: 2, height: 50, displayValue: false, margin: 5
      });
      packageBarcodeBase64 = packageCanvas.toDataURL("image/png");
    } catch (err) {
      console.error('Barcode generation error:', err);
      showError('Barkod oluşturulurken hata oluştu');
      return;
    }

    const senderCompany = activeCompany || orderToUse?.company || {};
    const orderNumber = orderToUse?.orderNumber || '';
    const shippingAddress = orderToUse?.shippingAddress || {};
    const today = new Date().toLocaleDateString('tr-TR');
    const productsHtml = orderToUse?.products?.map(product => `
      <tr><td>${product?.name || ''}</td><td style="text-align:center">${product?.quantity || ''}</td></tr>
    `).join('') || '';

    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Kargo Etiketi - ${trackingNumber}</title>
          <style>
            @page { size: 100mm 150mm; margin: 3mm; }
            @media print { body { margin: 0; padding: 0; } }
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: Arial, Helvetica, sans-serif; font-size: 10px; }
            .label { width: 94mm; border: 1px solid #333; margin: 0 auto; }
            .section { padding: 6px 8px; border-bottom: 1px solid #333; }
            .section:last-child { border-bottom: none; }
            .section-header { display: flex; justify-content: space-between; align-items: center; }
            .sender-name { font-weight: bold; font-size: 11px; }
            .date { font-size: 11px; }
            .field-label { color: #555; font-size: 9px; }
            .field-value { font-weight: bold; font-size: 10px; }
            .info-title { font-weight: bold; font-size: 10px; margin-bottom: 4px; }
            .info-row { display: flex; margin-bottom: 2px; }
            .info-key { width: 50px; font-size: 9px; font-weight: bold; }
            .info-val { flex: 1; font-size: 9px; }
            .order-row { font-size: 9px; padding: 4px 8px; border-bottom: 1px solid #333; background: #f5f5f5; }
            .barcode-section { padding: 6px 8px; border-bottom: 1px solid #333; }
            .barcode-label { font-size: 9px; font-weight: bold; margin-bottom: 2px; }
            .barcode-number { font-size: 11px; font-weight: bold; }
            .barcode-img { text-align: center; margin: 4px 0; }
            .barcode-img img { max-width: 100%; height: auto; }
            .package-section { padding: 6px 8px; }
            .package-footer { display: flex; justify-content: space-between; align-items: flex-end; }
            .package-info { font-size: 11px; font-weight: bold; }
            .products-table { width: 100%; border-collapse: collapse; font-size: 9px; }
            .products-table th, .products-table td { padding: 3px 4px; border: 1px solid #999; }
            .products-table th { background: #eee; text-align: left; font-weight: bold; }
          </style>
        </head>
        <body>
          <div class="label">
            <div class="section">
              <div class="section-header">
                <div>
                  <span class="field-label">Gönderici</span><br>
                  <span class="sender-name">${senderCompany?.name || ''}</span>
                </div>
                <span class="date">${today}</span>
              </div>
            </div>

            <div class="section">
              <div class="info-title">Alıcı Bilgileri</div>
              <div class="info-row"><span class="info-key">İsim</span><span class="info-val">: ${shippingAddress?.fullName || ''}</span></div>
              <div class="info-row"><span class="info-key">Telefon</span><span class="info-val">: ${shippingAddress?.phone || ''}</span></div>
              <div class="info-row"><span class="info-key">Adres</span><span class="info-val">: ${shippingAddress?.address || ''} ${shippingAddress?.district || ''} ${shippingAddress?.city || ''}</span></div>
            </div>

            ${orderNumber ? `<div class="order-row"><strong>Sipariş:</strong> ${orderNumber}</div>` : ''}

            ${productsHtml ? `
            <div class="section" style="padding: 4px 8px;">
              <table class="products-table">
                <thead><tr><th>Ürün</th><th style="width:40px;text-align:center">Adet</th></tr></thead>
                <tbody>${productsHtml}</tbody>
              </table>
            </div>
            ` : ''}

            ${hasTwoBarcodes ? `
            <div class="barcode-section">
              <div class="barcode-label">${trackingLabel} : <span class="barcode-number">${trackingNumber}</span></div>
              <div class="barcode-img"><img src="${integrationBarcodeBase64}" /></div>
            </div>
            <div class="package-section">
              <div class="barcode-label">${barcodeLabel} : <span class="barcode-number">${barcodeNumber}</span></div>
              <div class="package-footer">
                <div class="barcode-img" style="flex:1"><img src="${packageBarcodeBase64}" /></div>
                <div class="package-info">Paket : 1 / 1</div>
              </div>
            </div>
            ` : `
            <div class="package-section">
              <div class="barcode-label">${barcodeLabel} : <span class="barcode-number">${barcodeNumber}</span></div>
              <div class="barcode-img"><img src="${packageBarcodeBase64}" /></div>
            </div>
            `}
          </div>
          <script>setTimeout(() => window.print(), 500);</script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const handlePrintLabel = () => {
    if (currentOrder?.shipment?.barcode) printBarcode(currentOrder.shipment.barcode);
    else showError('Yazdırılabilir barkod bulunamadı');
  };

  const handleUpdateTracking = async () => {
    try {
      setLoading(true);
      const response = await apiRequest(`/shipping/orders/${currentOrder.id}/tracking`, { method: 'POST' });
      if (response.success) {
        showSuccess('Kargo takip bilgileri güncellendi');
        onSuccess(response.shipment, true);
        setTimeout(() => triggerNotificationRefresh(), 1000);
      } else {
        throw new Error(response.message || 'Kargo takip bilgileri güncellenirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Failed to update tracking:', error);
      if (error.apiError) showServerError();
      else showError(error.message || 'Kargo takip bilgileri güncellenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelShipment = async () => {
    if (!window.confirm('Kargo gönderisini iptal etmek istediğinize emin misiniz?')) return;
    try {
      setLoading(true);
      const response = await apiRequest(`/shipping/orders/${currentOrder.id}/cancel-shipment`, { method: 'POST' });
      if (response.success) {
        showSuccess('Kargo gönderisi başarıyla iptal edildi');
        onSuccess(null, true);
        setTimeout(() => triggerNotificationRefresh(), 1000);
      } else {
        throw new Error(response.message || 'Kargo gönderisi iptal edilirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Failed to cancel shipment:', error);
      if (error.apiError) showServerError();
      else showError(error.message || 'Kargo gönderisi iptal edilirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const hasActiveShipment = Boolean(currentOrder?.shipment?.status && currentOrder.shipment.status !== 'PENDING' && currentOrder.shipment.status !== 'CANCELLED');
  const showShipmentForm = !currentOrder?.shipment?.status || currentOrder.shipment.status === 'PENDING' || currentOrder.shipment.status === 'CANCELLED';

  const getShipmentStatusStyle = (status) => SHIPMENT_STATUS_STYLES[status] || { bg: '#f8f9fa', text: '#6c757d', icon: 'question-circle' };
  const getShipmentStatusText = (status) => SHIPMENT_STATUS_TEXT[status] || status;

  const footer = hasActiveShipment ? (
    <div className="d-flex justify-content-end gap-2 w-100">
      <button type="button" className="btn btn-danger" onClick={handleCancelShipment} disabled={loading}>Gönderiyi İptal Et</button>
      <button type="button" className="btn btn-primary" onClick={handleUpdateTracking} disabled={loading}>
        {loading ? <><span className="spinner-border spinner-border-sm me-2" />Güncelleniyor...</> : 'Takip Bilgilerini Güncelle'}
      </button>
    </div>
  ) : (
    <div className="d-flex justify-content-end gap-2 w-100">
      <button type="button" className="btn btn-light" onClick={onClose}>İptal</button>
      <button type="button" className="btn btn-primary" disabled={loading} onClick={handleSubmit}>
        {loading ? <><span className="spinner-border spinner-border-sm me-2" />Oluşturuluyor...</> : 'Gönderi Oluştur'}
      </button>
    </div>
  );

  return (
    <ActionDrawer
      isOpen={isOpen}
      onClose={onClose}
      title={currentOrder?.shipment?.status && !showShipmentForm ? 'Kargo Detayları' : 'Kargo Gönderisi Oluştur'}
      width="700px"
      footer={footer}
    >
      <div className="shipment-modal-content">
        {currentOrder?.shipment?.status && (
          <div className="alert alert-info py-2 mb-3">
            <div className="d-flex align-items-center">
              <i className="fas fa-info-circle me-2"></i>
              <div className="d-flex align-items-center gap-1">
                <strong>Durum: </strong>
                <ShipmentStatusWithInfo status={currentOrder.shipment.status} />
              </div>
            </div>
          </div>
        )}

        {currentOrder?.shipment?.trackingHistory?.length > 0 && (
          <div className="mb-4">
            <h6 className="mb-3 fw-bold"><i className="fas fa-history me-2"></i>Kargo Takip Geçmişi</h6>
            <div className="tracking-timeline">
              {currentOrder.shipment.trackingHistory.map((history, index) => (
                <div key={index} className="tracking-item">
                  <div className="tracking-icon"><i className={`fas fa-${getShipmentStatusStyle(history.status).icon}`}></i></div>
                  <div className="tracking-content p-2 rounded bg-light">
                    <div className="fw-medium small">{getShipmentStatusText(history.status)}</div>
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>{new Date(history.timestamp).toLocaleString('tr-TR')}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {showShipmentForm ? (
          <div className="row g-3">
            {currentOrder?.products?.length > 0 && (
              <div className="col-12">
                <div className="card bg-light border-0 shadow-sm">
                  <div className="card-body p-2">
                    <table className="table table-sm table-hover align-middle mb-0">
                      <thead><tr><th>Ürün</th><th>Adet</th></tr></thead>
                      <tbody>
                        {currentOrder.products.map((product, index) => (
                          <tr key={index}>
                            <td>
                              <div className="d-flex align-items-center">
                                <img src={product.mainImage || placeholderImage} alt="" className="rounded me-2" style={{ width: '32px', height: '32px', objectFit: 'cover' }} />
                                <span className="small">{product.name}</span>
                              </div>
                            </td>
                            <td className="small">{product.quantity}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
            <div className="col-12"><hr className="my-1" /></div>
            <div className="col-12">
              <label className="form-label text-muted small fw-medium">Kargo Firması*</label>
              <select className="form-select" value={formData.carrierId} onChange={(e) => setFormData({ ...formData, carrierId: Number(e.target.value) })} required>
                <option value="">Seçiniz...</option>
                {carriers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div className="col-md-6">
              <label className="form-label text-muted small fw-medium">Ağırlık (kg)</label>
              <input type="number" step="0.1" className="form-control" value={formData.packageDetails.weight} onChange={(e) => setFormData({ ...formData, packageDetails: { ...formData.packageDetails, weight: e.target.value } })} />
            </div>
            <div className="col-md-6">
              <label className="form-label text-muted small fw-medium">Paket Boyutu</label>
              <select className="form-select" value={formData.packageDetails.size} onChange={(e) => setFormData({ ...formData, packageDetails: { ...formData.packageDetails, size: e.target.value } })}>
                <option value="">Seçiniz...</option>
                <option value="SMALL">Küçük</option><option value="MEDIUM">Orta</option><option value="LARGE">Büyük</option>
              </select>
            </div>
          </div>
        ) : (
          <div className="row g-3">
            <div className="col-md-6"><small className="text-muted d-block">Sipariş No</small><strong>#{currentOrder?.orderNumber}</strong></div>
            <div className="col-md-6"><small className="text-muted d-block">Kargo Firması</small><strong>{currentOrder?.shipment?.carrier?.name}</strong></div>
            <div className="col-md-6"><small className="text-muted d-block">Takip No</small><strong>{currentOrder?.shipment?.trackingNumber}</strong></div>
            <div className="col-md-6"><small className="text-muted d-block">Boyut / Ağırlık</small><strong>{{ SMALL: 'Küçük', MEDIUM: 'Orta', LARGE: 'Büyük' }[currentOrder?.shipment?.packageDetails?.size] || currentOrder?.shipment?.packageDetails?.size || '-'} / {currentOrder?.shipment?.packageDetails?.weight ? currentOrder.shipment.packageDetails.weight + 'kg' : '-'}</strong></div>
            {currentOrder.shipment?.barcode && (
              <div className="col-12 mt-3 text-center">
                <button type="button" className="btn btn-outline-primary w-100" onClick={handlePrintLabel}><i className="fas fa-barcode me-2" />Barkod Yazdır</button>
              </div>
            )}
          </div>
        )}
      </div>

      <style>{`
        .tracking-timeline { position: relative; padding-left: 20px; }
        .tracking-item { position: relative; padding-bottom: 1rem; }
        .tracking-item::before { content: ''; position: absolute; left: -14px; top: 15px; bottom: 0; width: 1px; background: #dee2e6; }
        .tracking-item:last-child::before { display: none; }
        .tracking-icon { position: absolute; left: -20px; width: 12px; height: 12px; background: #fff; border-radius: 50%; color: var(--kito-orange); font-size: 10px; }
      `}</style>
    </ActionDrawer>
  );
}

export default ShipmentModal;
